import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

function _clearLegacyLocalStorageToken() {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.removeItem("rooster.token");
    }
  } catch { /* localStorage unavailable */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const [legalRequired, setLegalRequired] = useState(false);
  const [legalStatusLoaded, setLegalStatusLoaded] = useState(false);
  // Fase 6.5B.3 — separate flag so RequireAuth can show a safe error card
  // instead of an endless spinner when /legal/status fails.
  const [legalStatusError, setLegalStatusError] = useState(false);

  const hydrateSession = useCallback(async (userData) => {
    if (userData && userData.theme) {
      try { localStorage.setItem("rostr.theme", userData.theme); } catch {}
    }
    setLegalStatusLoaded(false);
    setLegalStatusError(false);
    setUser(userData || false);
    let needsLegal = false;
    let statusResolved = false;
    let hadError = false;
    try {
      const s = await api.get("/legal/status");
      needsLegal = !!(s.data?.terms_required || s.data?.privacy_notice_required);
      statusResolved = true;
    } catch {
      hadError = true;
    }
    setLegalRequired(needsLegal);
    setLegalStatusLoaded(statusResolved);
    setLegalStatusError(hadError);
    return needsLegal;
  }, []);

  const bootstrap = useCallback(async () => {
    _clearLegacyLocalStorageToken();
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      await hydrateSession(data);
    } catch (e) {
      setLegalRequired(false);
      setLegalStatusLoaded(true);
      setLegalStatusError(false);
      setUser(false);
    }
  }, [hydrateSession]);

  useEffect(() => {
    bootstrap();
    const on = () => setLegalRequired(true);
    if (typeof window !== "undefined") window.addEventListener("rostr:legal-required", on);
    return () => { if (typeof window !== "undefined") window.removeEventListener("rostr:legal-required", on); };
  }, [bootstrap]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Phase 8.2 — MFA-enabled accounts: the login response carries no
      // user object; we forward the challenge context to the caller so
      // the Login page can render the code-entry step.
      if (data?.status === "mfa_required") {
        return {
          ok: false,
          mfa_required: true,
          challenge_id: data.challenge_id,
          expires_in: data.expires_in,
        };
      }
      await hydrateSession(data.user);
      return { ok: true };
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      return { ok: false, error: msg };
    }
  };

  // Phase 8.2 — complete an outstanding MFA login challenge.
  const completeMfaChallenge = async (challenge_id, code, use_recovery_code = false) => {
    setError("");
    try {
      const { data } = await api.post("/auth/mfa/challenge/verify", {
        challenge_id, code, use_recovery_code,
      });
      await hydrateSession(data.user);
      return { ok: true };
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) return { ok: false, rate_limited: true, retry_after: detail?.retry_after || 60 };
      const msg = formatApiErrorDetail(detail) || e.message;
      return { ok: false, error: msg };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    _clearLegacyLocalStorageToken();
    sessionStorage.removeItem("rostr.welcome.shown");
    setLegalRequired(false);
    setLegalStatusLoaded(false);
    setLegalStatusError(false);
    setUser(false);
  };

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      await hydrateSession(data);
    } catch {}
  }, [hydrateSession]);

  // Fase 6.5B.3 — bounded manual retry from the error card. Attempts to
  // re-fetch /legal/status ONCE using the currently-known user; the
  // resolve/reject updates the same three flags atomically. There is no
  // auto-retry loop; the user has to press the button.
  const retryLegalStatus = useCallback(async () => {
    if (!user) return;
    setLegalStatusError(false);
    setLegalStatusLoaded(false);
    try {
      const s = await api.get("/legal/status");
      setLegalRequired(!!(s.data?.terms_required || s.data?.privacy_notice_required));
      setLegalStatusLoaded(true);
      setLegalStatusError(false);
    } catch {
      setLegalStatusLoaded(false);
      setLegalStatusError(true);
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user,
      login,
      completeMfaChallenge,
      logout,
      error,
      refreshUser,
      setUser,
      hydrateSession,
      legalRequired,
      setLegalRequired,
      legalStatusLoaded,
      legalStatusError,
      retryLegalStatus,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
